import { AlertTriangle } from "lucide-react";
import useAutoScrollIntoView from "../../hooks/useAutoScrollIntoView";
import useAutoHide from "../../hooks/useAutoHide";
import MessageCountdown from "../common/MessageCountdown";
import { toDisplayString } from "../../utils/normalizeApiError";

export default function ErrorAlert({
  title = "Please check this",
  message,
  hint,
  onDismiss,
  onRetry,
  retryLabel = "Try again",
  autoScroll = true,
}) {
  const displayMessage = toDisplayString(message);
  const displayHint = toDisplayString(hint);
  const displayTitle = toDisplayString(title, "Please check this");

  const scrollRef = useAutoScrollIntoView(displayMessage || "", {
    enabled: autoScroll && Boolean(displayMessage),
  });

  // Closes 30s after it appears; a new or re-raised message shows again.
  const { visible, secondsLeft, close } = useAutoHide(displayMessage ? `${displayTitle}|${displayMessage}` : "");

  if (!displayMessage || !visible) return null;

  return (
    <div
      ref={scrollRef}
      data-auto-scroll-alert={autoScroll ? "true" : "false"}
      role="alert"
      className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 shadow-sm outline-none scroll-mt-6"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700 ring-1 ring-amber-200">
          <AlertTriangle className="h-5 w-5" strokeWidth={1.75} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-950">{displayTitle}</p>
          <p className="mt-1 text-sm leading-relaxed text-amber-900">{displayMessage}</p>
          {displayHint ? <p className="mt-2 text-xs leading-relaxed text-amber-800/90">{displayHint}</p> : null}
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="mt-3 inline-flex items-center rounded-lg border border-amber-200 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100/60"
            >
              {retryLabel}
            </button>
          ) : null}
        </div>
        <MessageCountdown
          seconds={secondsLeft}
          onClose={() => {
            close();
            onDismiss?.();
          }}
        />
      </div>
    </div>
  );
}
